<script setup lang="ts">
import { computed } from 'vue';
import { BBtn, BTooltip } from '@/design-system';
import rfidHwTooltipImg from '@/assets/img/rfid-hw-tooltip.webp';
import rfidTagTooltipImg from '@/assets/img/rfid-tag-tooltip.webp';
import Step1Svg from '@/assets/img/demo-landing/step-1.svg?component';
import Step2Svg from '@/assets/img/demo-landing/step-2.svg?component';
import { useTranslation } from '@/composables';
import { useDemoOnboardingStore } from '@/stores/demoOnboardingStore';

const { t } = useTranslation();
const store = useDemoOnboardingStore();

const isStep1 = computed(() => store.currentStep === 1);
const isStep2 = computed(() => store.currentStep === 2);

const handleContinue = (): void => {
    store.nextStep();
};
</script>

<template>
    <VDialog
        :model-value="store.isOpen"
        :max-width="840"
        persistent
        no-click-animation
        scrim="rgba(0,0,0,0.6)"
    >
        <VCard
            class="demo-onboarding-card"
            rounded="xl"
            elevation="0"
        >
            <div class="demo-onboarding-content">
                <div class="demo-onboarding-illustration">
                    <Step1Svg
                        v-if="isStep1"
                        class="girl-svg"
                    />
                    <Step2Svg
                        v-else
                        class="girl-svg"
                    />
                </div>

                <div class="demo-onboarding-body">
                    <Transition
                        name="step-fade"
                        mode="out-in"
                    >
                        <div
                            v-if="isStep1"
                            key="step1"
                            class="step-content step1-content"
                        >
                            <h2 class="demo-onboarding-title text-primary-darken-1">
                                {{ t('demoOnboarding.step1.title') }}
                            </h2>
                            <p class="demo-onboarding-subtitle">
                                {{ t('demoOnboarding.step1.subtitle') }}
                            </p>
                            <ol class="demo-onboarding-list">
                                <li>
                                    {{ t('demoOnboarding.step1.item1_before') }}
                                    <BTooltip
                                        location="end"
                                        content-class="rfid-hw-tooltip-content"
                                        :max-width="400"
                                    >
                                        <template #activator="{ props: tooltipProps }">
                                            <span
                                                v-bind="tooltipProps"
                                                class="text-underline tooltip-trigger"
                                            >
                                                {{ t('demoOnboarding.step1.item1_highlight') }}
                                            </span>
                                        </template>
                                        <img
                                            :src="rfidTagTooltipImg"
                                            alt="RFID hardware"
                                            class="rfid-hw-tooltip-img"
                                        />
                                    </BTooltip>
                                    {{ t('demoOnboarding.step1.item1_after') }}
                                </li>
                                <li>
                                    {{ t('demoOnboarding.step1.item2_before') }}
                                    <BTooltip
                                        location="end"
                                        content-class="rfid-hw-tooltip-content"
                                        :max-width="400"
                                    >
                                        <template #activator="{ props: tooltipProps }">
                                            <span
                                                v-bind="tooltipProps"
                                                class="text-underline tooltip-trigger"
                                            >
                                                {{ t('demoOnboarding.step1.item2_highlight') }}
                                            </span>
                                        </template>
                                        <img
                                            :src="rfidHwTooltipImg"
                                            alt="RFID hardware"
                                            class="rfid-hw-tooltip-img"
                                        />
                                    </BTooltip>
                                    {{ t('demoOnboarding.step1.item2_after') }}
                                </li>
                            </ol>
                            <p class="demo-onboarding-bold">
                                {{ t('demoOnboarding.step1.bold') }}
                            </p>
                            <p class="demo-onboarding-note">
                                {{ t('demoOnboarding.step1.note') }}
                            </p>
                        </div>

                        <div
                            v-else-if="isStep2"
                            key="step2"
                            class="step-content"
                        >
                            <h2 class="demo-onboarding-title text-primary-darken-1">
                                {{ t('demoOnboarding.step2.title') }}
                            </h2>
                            <p class="demo-onboarding-subtitle">
                                {{ t('demoOnboarding.step2.subtitle') }}
                            </p>
                            <ol class="demo-onboarding-list">
                                <li>{{ t('demoOnboarding.step2.item1') }}</li>
                                <li>{{ t('demoOnboarding.step2.item2') }}</li>
                                <li>{{ t('demoOnboarding.step2.item3') }}</li>
                                <li>{{ t('demoOnboarding.step2.item4') }}</li>
                            </ol>
                        </div>
                    </Transition>

                    <div class="demo-onboarding-footer">
                        <BBtn
                            color="ai-red"
                            rounded
                            class="text-none btn-continue"
                            @click="handleContinue"
                        >
                            {{ t('common.continue') }}
                            <VIcon
                                end
                                size="small"
                            >
                                mdi-chevron-right
                            </VIcon>
                        </BBtn>

                        <span class="step-indicator">{{ store.stepLabel }}</span>
                    </div>
                </div>
            </div>
        </VCard>
    </VDialog>
</template>

<style scoped lang="scss">
@keyframes dialog-card-enter {
    from {
        opacity: 0;
        transform: scale(0.88) translateY(28px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes slide-in-left {
    from {
        opacity: 0;
        transform: translateX(-40px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateX(0) scale(1);
    }
}

@keyframes fade-up {
    from {
        opacity: 0;
        transform: translateY(18px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.demo-onboarding-card {
    overflow: hidden;
    animation: dialog-card-enter 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.demo-onboarding-content {
    display: flex;
    align-items: center;
    min-height: 400px;
}

.demo-onboarding-illustration {
    flex: 0 0 290px;
    display: flex;
    justify-content: center;
    overflow: hidden;
    animation: slide-in-left 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;

    .girl-svg {
        height: auto;
    }
}

.demo-onboarding-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 32px 32px 24px;
}

.step-content {
    flex: 1;
}

.demo-onboarding-title {
    font-size: 1.35rem;
    font-weight: 700;
    margin-bottom: 32px;
    line-height: 1.3;
}

.demo-onboarding-subtitle {
    font-size: 0.95rem;
    font-weight: 700;
    color: rgb(var(--v-theme-grey-700));
    margin-bottom: 16px;
}

.demo-onboarding-list {
    padding-left: 20px;
    margin-bottom: 16px;

    li {
        font-size: 0.9rem;
        color: rgb(var(--v-theme-grey-700));
        margin-bottom: 8px;
        line-height: 1.5;
    }
}

.demo-onboarding-bold {
    font-weight: 700;
    font-size: 0.95rem;
    color: rgb(var(--v-theme-grey-700));
    margin-bottom: 4px;
}

.demo-onboarding-note {
    font-size: 0.9rem;
    color: rgb(var(--v-theme-grey-700));
    line-height: 1.5;
}

.step1-content {
    .demo-onboarding-title {
        animation: fade-up 0.45s ease-out 0.3s both;
    }

    .demo-onboarding-subtitle {
        animation: fade-up 0.45s ease-out 0.4s both;
    }

    .demo-onboarding-list {
        animation: fade-up 0.45s ease-out 0.5s both;
    }

    .demo-onboarding-bold {
        animation: fade-up 0.4s ease-out 0.58s both;
    }

    .demo-onboarding-note {
        animation: fade-up 0.4s ease-out 0.64s both;
    }
}

.demo-onboarding-footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 24px;
    animation: fade-up 0.4s ease-out 0.7s both;
}

.btn-continue {
    padding-inline: 24px;
}

.step-indicator {
    font-size: 0.85rem;
    color: rgb(var(--v-theme-grey-400));
    font-weight: 500;
}

.step-fade-enter-active,
.step-fade-leave-active {
    transition: opacity 250ms ease;
}

.step-fade-enter-from,
.step-fade-leave-to {
    opacity: 0;
}

.text-underline {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.tooltip-trigger {
    cursor: help;
}

.rfid-hw-tooltip-img {
    display: block;
    width: 100%;
    max-width: 400px;
    height: auto;
    border-radius: 8px;
}

:deep() {
    // Floating animation
    @keyframes float-a {
        0% {
            transform: translateY(0px) rotate(0deg);
        }
        25% {
            transform: translateY(-4px) rotate(2deg);
        }
        75% {
            transform: translateY(3px) rotate(-1deg);
        }
        100% {
            transform: translateY(0px) rotate(0deg);
        }
    }
    @keyframes float-b {
        0% {
            transform: translateY(0px) rotate(0deg);
        }
        30% {
            transform: translateY(3px) rotate(-2deg);
        }
        70% {
            transform: translateY(-4px) rotate(1deg);
        }
        100% {
            transform: translateY(0px) rotate(0deg);
        }
    }
    @keyframes float-c {
        0% {
            transform: translateX(0px) translateY(0px);
        }
        40% {
            transform: translateX(2px) translateY(-3px);
        }
        80% {
            transform: translateX(-2px) translateY(2px);
        }
        100% {
            transform: translateX(0px) translateY(0px);
        }
    }
    @keyframes float-d {
        0% {
            transform: translateX(0px) translateY(0px) scale(1);
        }
        50% {
            transform: translateX(-3px) translateY(-2px) scale(1.05);
        }
        100% {
            transform: translateX(0px) translateY(0px) scale(1);
        }
    }
    @keyframes pulse-dot {
        0% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.3);
            opacity: 0.7;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    #deco-top-center {
        animation: float-a 3.8s ease-in-out infinite;
        animation-delay: 0s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-top-right {
        animation: float-b 4.2s ease-in-out infinite;
        animation-delay: 0.5s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-left-upper {
        animation: float-c 3.5s ease-in-out infinite;
        animation-delay: 1s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-left-mid {
        animation: float-a 4.6s ease-in-out infinite;
        animation-delay: 0.3s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-right-upper {
        animation: float-b 3.9s ease-in-out infinite;
        animation-delay: 0.8s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-right-mid {
        animation: float-d 4.4s ease-in-out infinite;
        animation-delay: 1.3s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-right-lower {
        animation: float-c 3.6s ease-in-out infinite;
        animation-delay: 0.2s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-left-lower {
        animation: float-b 4.8s ease-in-out infinite;
        animation-delay: 0.7s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-left-lower {
        animation: pulse-dot 2.8s ease-in-out infinite;
        animation-delay: 0.4s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-far-left {
        animation: pulse-dot 3.2s ease-in-out infinite;
        animation-delay: 1.1s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-left-upper {
        animation: pulse-dot 2.5s ease-in-out infinite;
        animation-delay: 0.9s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-right {
        animation: pulse-dot 3s ease-in-out infinite;
        animation-delay: 0.6s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-right-upper {
        animation: pulse-dot 2.7s ease-in-out infinite;
        animation-delay: 1.4s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-top-center {
        animation: float-a 3.8s ease-in-out infinite;
        animation-delay: 0s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-top-right {
        animation: float-b 4.2s ease-in-out infinite;
        animation-delay: 0.5s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-left-mid {
        animation: float-a 4.6s ease-in-out infinite;
        animation-delay: 0.3s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-right-upper {
        animation: float-b 3.9s ease-in-out infinite;
        animation-delay: 0.8s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-right-lower {
        animation: float-c 3.6s ease-in-out infinite;
        animation-delay: 0.2s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-left-lower {
        animation: float-b 4.8s ease-in-out infinite;
        animation-delay: 0.7s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-right-upper {
        animation: pulse-dot 2.8s ease-in-out infinite;
        animation-delay: 0.4s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-right-mid {
        animation: pulse-dot 3.2s ease-in-out infinite;
        animation-delay: 1.1s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-left-upper {
        animation: pulse-dot 2.5s ease-in-out infinite;
        animation-delay: 0.9s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-left-mid {
        animation: pulse-dot 3s ease-in-out infinite;
        animation-delay: 0.6s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-left-lower {
        animation: pulse-dot 2.7s ease-in-out infinite;
        animation-delay: 1.4s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dot-far-left {
        animation: pulse-dot 3.4s ease-in-out infinite;
        animation-delay: 0.2s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dots-right-grey {
        animation: float-c 5s ease-in-out infinite;
        animation-delay: 1s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dots-left-grey {
        animation: float-d 4.5s ease-in-out infinite;
        animation-delay: 0.6s;
        transform-box: fill-box;
        transform-origin: center;
    }

    #deco-dots-top-grey {
        animation: float-a 4.8s ease-in-out infinite;
        animation-delay: 1.3s;
        transform-box: fill-box;
        transform-origin: center;
    }
}
</style>

<style lang="scss">
.rfid-hw-tooltip-content {
    background-color: rgb(var(--v-theme-surface)) !important;
    padding: 8px !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}
</style>
